import { Routes, CanActivateFn, Router, UrlTree } from '@angular/router';
import { MemberGuard, ProgramDirectorGuard, CxAdminGuard, redirectIfAuthenticated, EditProgramGuard } from './guards';
import { VacantPositionResolver } from './pages/vacant-position/details/vacant-position.resolver';
import { VacantPositionFieldsResolver } from './pages/vacant-position/details/vacant-position-fields.resolver';
import { ProgramResolver } from './pages/program/program.resolver';
import { SpecialtyResolver } from './pages/specialty/specialty.resolver';
import { HomeComponent } from "./pages/home/home.component";
import { inject } from '@angular/core';
import { PromotionService } from './services';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// Redirect away from dashboard routes when system-wide updates are disabled
export const DisableUpdatesGuard: CanActivateFn = () => {
  const promotionService = inject(PromotionService);
  const router = inject(Router);
  return promotionService.getSystemWideMessage().pipe(
    map((promos) => {
      const disabled = !!promos?.[0]?.disableUpdates;
      return disabled ? (router.createUrlTree(['/']) as UrlTree) : true;
    }),
    catchError(() => of(true))
  );
};

export const routes: Routes = [
  { path: '', pathMatch: 'full', component: HomeComponent, canMatch: [redirectIfAuthenticated] },
  { path: 'fellowship', pathMatch: 'full', component: HomeComponent, canMatch: [redirectIfAuthenticated] },
  { path: 'home', component: HomeComponent },
  {
    path: 'search/list',
    pathMatch: 'full',
    loadComponent: () => import('./pages/search').then(m => m.ProgramSearchComponent),
    data: { load: true }
  },
  {
    path: 'search/map',
    pathMatch: 'full',
    loadComponent: () => import('./pages/search').then(m => m.ProgramSearchComponent),
    data: { load: true }
  },
  {
    path: 'transition',
    loadComponent: () => import('./pages/transition/transition.component').then(c => c.TransitionComponent)
  },
  {
    path: 'contact',
    loadComponent: () => import('./pages/contact/contact.component').then(c => c.ContactPageComponent)
  },
  {
    path: 'faq',
    loadComponent: () => import('./pages/faq/faq.component').then(c => c.FaqComponent)
  },
  {
    path: 'calculator',
    loadComponent: () => import('./pages/calculator-landing/calculator-landing.component').then(c => c.CalculatorLandingComponent)
  },
  {
    path: 'cx-admin-portal',
    canActivate: [CxAdminGuard],
    loadComponent: () => import('./pages/cx-admin-portal/password-reset.component').then(c => c.PasswordResetComponent),
  },
  {
    path: 'road-to-residency-guide',
    children: [
      {
        path: 'resources',
        canActivate: [MemberGuard],
        loadComponent: () => import('./pages/road-to-residency-guide/resources/resources.component').then(l => l.RoadToResidencyGuideResourcesComponent)
      },
      {
        path: 'timeline',
        canActivate: [MemberGuard],
        loadComponent: () => import('./pages/road-to-residency-guide/timeline/timeline.component').then(l => l.RoadToResidencyGuideTimelineComponent)
      },
      {
        path: 'match',
        canActivate: [MemberGuard],
        loadComponent: () => import('./pages/road-to-residency-guide/match/match.component').then(l => l.RoadToResidencyGuideMatchComponent)
      },
      {
        path: 'application-interview',
        canActivate: [MemberGuard],
        loadComponent: () => import('./pages/road-to-residency-guide/interview/interview.component').then(l => l.RoadToResidencyGuideInterviewComponent)
      },
      {
        path: 'research',
        canActivate: [MemberGuard],
        loadComponent: () => import('./pages/road-to-residency-guide/research/research.component').then(l => l.RoadToResidencyGuideResearchComponent)
      },
      {
        path: 'accreditation',
        canActivate: [MemberGuard],
        loadComponent: () => import('./pages/road-to-residency-guide/accreditation/accreditation.component').then(l => l.RoadToResidencyGuideAccreditationComponent)
      },
      {
        path: 'overview',
        canActivate: [MemberGuard],
        loadComponent: () => import('./pages/road-to-residency-guide/overview/overview.component').then(l => l.RoadToResidencyGuideOverviewComponent)
      },
      {
        path: '',
        loadComponent: () => import('./pages/road-to-residency-guide/road-to-residency-guide.component').then(c => c.RoadToResidencyGuideComponent),
      },
      {
        path: '**',
        loadComponent: () => import('./pages/road-to-residency-guide/road-to-residency-guide.component').then(l => l.RoadToResidencyGuideComponent)
      }
    ]
  },
  {
    path: 'memberbenefits',
    loadComponent: () => import('./pages/members-benefits/members-benefits.component').then(c => c.MembersBenefitsComponent)
  },
  {
    path: 'dashboard',
    canActivate: [MemberGuard, DisableUpdatesGuard],
    children: [
      { path: 'programs', redirectTo: 'programs/list', pathMatch: 'full' },
      {
        path: 'programs/:viewMode',
        loadComponent: () => import('./pages/my-saved-programs/my-saved-programs.component').then(c => c.MySavedProgramsComponent),
      },
      {
        path: 'compare',
        loadComponent: () => import('./pages/compare-program/compare-program.component').then(c => c.CompareProgramComponent)
      },
    ]
  },
  {
    path: 'dashboard-landing',
    canActivate: [MemberGuard],
    pathMatch: 'full',
    loadComponent: () => import('./pages/dashboard/dashboard.component').then(d => d.DashboardPageComponent),
  },
  {
    path: 'director',
    canActivate: [ProgramDirectorGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/director/director.component').then(d => d.DirectorComponent),
        canActivate: [ProgramDirectorGuard],
      },
      {
        path: 'edit-program/:programNumber',
        loadComponent: () => import('./pages/director/edit-program/edit-program.component').then(d => d.EditProgramComponent),
        canActivate: [ProgramDirectorGuard, EditProgramGuard]
      }
    ]
  },
  {
    path: 'institution',
    children: [
      {
        path: ':institutionId',
        loadComponent: () => import('./pages/institutions/details/institution-details.component').then(i => i.InstitutionDetailsComponent),
      },
      {
        path: '',
        loadComponent: () => import('./pages/institutions/directory/institution-directory.component').then(i => i.InstitutionDirectoryComponent),
      }
    ],
    data: { load: true }
  },
  {
    path: 'program-resources-form',
    loadComponent: () => import('./pages/program-resources-form/program-resources-form.component').then(p => p.ProgramResourcesFormComponent)
  },
  {
    path: 'obgyn-aci-program-survey',
    loadComponent: () => import('./pages/aci/aci-weights-survey/aci-weights-survey.component').then(a => a.AciWeightsSurveyComponent)
  },
  {
    path: 'residency-alignment',
    children: [
      {
        path: '',
        redirectTo: 'aci',
        pathMatch: 'full'
      },
      {
        path: 'aci',
        loadComponent: () => import('./pages/aci/aci.component').then(a => a.AciComponent),
        children: [
          {
            path: 'questionnaire',
            loadComponent: () => import('./pages/aci/form/aci-form.component').then(a => a.AciFormComponent),
            children: [
              {
                path: 'question1',
                loadComponent: () => import('./pages/aci/form/aci-form.component').then(a => a.AciFormComponent),
                pathMatch: 'full'
              },
              {
                path: 'question2',
                loadComponent: () => import('./pages/aci/form/aci-form.component').then(a => a.AciFormComponent),
                pathMatch: 'full'
              },
              {
                path: 'question3',
                loadComponent: () => import('./pages/aci/form/aci-form.component').then(a => a.AciFormComponent),
                pathMatch: 'full'
              },
              {
                path: 'question4',
                loadComponent: () => import('./pages/aci/form/aci-form.component').then(a => a.AciFormComponent),
                pathMatch: 'full'
              },
              {
                path: 'question5',
                loadComponent: () => import('./pages/aci/form/aci-form.component').then(a => a.AciFormComponent),
                pathMatch: 'full'
              },
              {
                path: 'question6',
                loadComponent: () => import('./pages/aci/form/aci-form.component').then(a => a.AciFormComponent),
                pathMatch: 'full'
              },
              {
                path: 'preview',
                loadComponent: () => import('./pages/aci/form/aci-form.component').then(a => a.AciFormComponent),
                pathMatch: 'full'
              }
            ],
          }
        ]
      },
    ]
  },
  {
    path: 'residency-calculator',
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/calculator/introduction/introduction.component').then(a => a.IntroductionComponent)
      },
      {
        path: '',
        loadComponent: () => import('./pages/calculator/steps/steps.component').then(a => a.StepsComponent),
        children: [
          {
            path: 'step-1',
            loadComponent: () => import('./pages/calculator/step1/step1.component').then(a => a.Step1Component),
          },
          {
            path: 'step-2',
            loadComponent: () => import('./pages/calculator/step2/step2.component').then(a => a.Step2Component)
          },
          {
            path: 'step-3',
            loadComponent: () => import('./pages/calculator/step3/step3.component').then(a => a.Step3Component),
          },
          {
            path: 'step-4',
            loadComponent: () => import('./pages/calculator/step4/step4.component').then(a => a.Step4Component),
          },
          {
            path: 'step-5',
            loadComponent: () => import('./pages/calculator/step5/step5.component').then(a => a.Step5Component),
          },
        ]
      },
      {
        path: 'result',
        loadComponent: () => import('./pages/calculator/result/result.component').then(a => a.ResultComponent),
      }
    ]
  },
  {
    path: 'search',
    loadComponent: () => import('./pages/search/search.component').then(s => s.ProgramSearchComponent),
    children: [
      {
        path: 'map',
        loadComponent: () => import('./pages/search/search.component').then(m => m.ProgramSearchComponent)
      },
      {
        path: 'map/aci',
        loadComponent: () => import('./pages/search/search.component').then(m => m.ProgramSearchComponent)
      },
      {
        path: 'list',
        loadComponent: () => import('./pages/search/search.component').then(l => l.ProgramSearchComponent)
      },
      {
        path: 'list/aci',
        loadComponent: () => import('./pages/search/search.component').then(l => l.ProgramSearchComponent)
      },
    ], data: { load: true }
  },
  {
    path: 'specialty',
    loadComponent: () => import('./pages/specialty/specialty-landing/specialty-landing.component').then(l => l.SpecialtyLandingComponent),
    data: { load: true }
  },
  {
    // Tab switching (e.g. 'training-information') is handled in-component via
    // Location.go(), not Router navigation, so this and the sibling route below
    // are flat leaf routes (same pattern as 'program/:pgmNbr/...') rather than
    // children — SpecialtyComponent has no <router-outlet> to host a child route.
    path: 'specialty/:slug',
    loadComponent: () => import('./pages/specialty/specialty.component').then(m => m.SpecialtyComponent),
    resolve: { specialty: SpecialtyResolver },
  },
  {
    path: 'specialty/:slug/training-information',
    loadComponent: () => import('./pages/specialty/specialty.component').then(m => m.SpecialtyComponent),
    resolve: { specialty: SpecialtyResolver },
  },
  {
    path: 'program/:pgmNbr',
    loadComponent: () => import('./pages/program/program.component').then(m => m.ProgramComponent),
    resolve: { program: ProgramResolver },
  },
  {
    path: 'program/:pgmNbr/program-work-schedule',
    loadComponent: () => import('./pages/program/program.component').then(m => m.ProgramComponent),
    resolve: { program: ProgramResolver },
  },
  {
    path: 'program/:pgmNbr/features-benefits',
    loadComponent: () => import('./pages/program/program.component').then(m => m.ProgramComponent),
    resolve: { program: ProgramResolver },
  },
  {
    path: 'program/:pgmNbr/life-culture',
    loadComponent: () => import('./pages/program/program.component').then(m => m.ProgramComponent),
    resolve: { program: ProgramResolver },
  },
  {
    path: 'vacant-position',
    loadComponent: () => import('./pages/vacant-position/vacant-position-wrapper.component').then(v => v.VacantPositionWrapperComponent),
    children: [
      { path: '', redirectTo: 'list', pathMatch: 'full' },
      {
        path: 'list',
        loadComponent: () => import('./pages/vacant-position/list/list.component').then(v => v.VacantPositionListComponent),
      },
      {
        path: 'map',
        loadComponent: () => import('./pages/vacant-position/map/map.component').then(v => v.VacantPositionMapComponent),
      },
      {
        path: ':vacantPositionTitle',
        loadComponent: () => import('./pages/vacant-position/details/vacant-position.component').then(v => v.VacantPositionComponent),
        resolve: {
          vacantPosition: VacantPositionResolver,
          fields: VacantPositionFieldsResolver
        },
        providers: [
          VacantPositionFieldsResolver,
          VacantPositionResolver
        ]
      }
    ],
    data: { load: true }
  },
  {
    path: 'video-series',
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/video-gallery/video-series/video-series.component').then(v => v.VideoSeriesComponent),
      },
      {
        path: ':seriesTitle',
        loadComponent: () => import('./pages/video-gallery/video-series/video-series.component').then(v => v.VideoSeriesComponent),
      },
      {
        path: ':seriesTitle/video/:videoTitle',
        loadComponent: () => import('./pages/video-gallery/video-series/video-series.component').then(v => v.VideoSeriesComponent),
      },
      {
        path: 'tours/:videoTitleId',
        loadComponent: () => import('./pages/video-gallery/video-series/video-series.component').then(v => v.VideoSeriesComponent),
      },
    ],
    data: { load: true }
  },
  { path: 'video-gallery', redirectTo: 'video-series', pathMatch: 'full' },
  { path: 'freida', pathMatch: 'prefix', redirectTo: '/' },
  { path: 'Freida', pathMatch: 'prefix', redirectTo: '/' },
  {
    path: '404', pathMatch: 'full',
    loadComponent: () => import('./pages/not-found/not-found.component').then(m => m.NotFoundComponent)
  },
  {
    path: '**', pathMatch: 'full',
    loadComponent: () => import('./pages/not-found/not-found.component').then(m => m.NotFoundComponent)
  },
];
